import React, { useRef, useState } from 'react';
import { Download, Loader2, Upload } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import useApi from '../api';
import useAnalytics from '../analytics/analytics';
import toastConfig from '../config/Toast';

interface ImportValidationError {
  row: number | null;
  field: string;
  message: string;
}

interface ImportValidationResult {
  valid: boolean;
  row_count: number;
  preview: Array<{
    question_text: string;
    answer_text: string;
  }>;
  errors: ImportValidationError[];
}

interface ImportQuestionsButtonProps {
  onImportStarted?: () => void;
}

const ImportQuestionsButton: React.FC<ImportQuestionsButtonProps> = ({ onImportStarted }) => {
  const { getBlob, post } = useApi();
  const { captureEvent } = useAnalytics();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [validation, setValidation] = useState<ImportValidationResult | null>(null);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const buildFormData = (csvFile: File) => {
    const formData = new FormData();
    formData.append('file', csvFile);
    return formData;
  };

  const clearImportState = () => {
    setFile(null);
    setValidation(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      clearImportState();
    }
  };

  const downloadTemplate = async () => {
    try {
      setIsDownloadingTemplate(true);
      const response = await getBlob('/questions/import-template');
      const url = window.URL.createObjectURL(response.blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'questions-import-template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      captureEvent('download_questions_import_template', {});
    } catch (error) {
      console.error('Error downloading question import template:', error);
      toast.error('Failed to download import template', toastConfig);
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const validateFile = async (csvFile: File) => {
    try {
      setIsValidating(true);
      const response = await post('/questions/import/validate', buildFormData(csvFile));
      setValidation(response.data);
      captureEvent('validate_questions_import', {
        valid: response.data.valid,
        row_count: response.data.row_count,
      });
    } catch (error) {
      console.error('Error validating question import:', error);
      setValidation(null);
      toast.error('Failed to validate CSV', toastConfig);
    } finally {
      setIsValidating(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
    setValidation(null);

    if (selectedFile) {
      await validateFile(selectedFile);
    }
  };

  const startImport = async () => {
    if (!file || !validation?.valid) {
      return;
    }

    try {
      setIsImporting(true);
      const response = await post('/questions/import', buildFormData(file));
      toast.success(`Import started for ${response.data.row_count} questions`, toastConfig);
      captureEvent('import_questions', {
        row_count: response.data.row_count,
      });
      setOpen(false);
      clearImportState();
      onImportStarted?.();
    } catch (error: any) {
      console.error('Error importing questions:', error);
      const detail = error?.detail;
      if (detail?.errors) {
        setValidation(detail);
      }
      toast.error('Failed to start question import', toastConfig);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="whitespace-nowrap">
        <Upload />
        Import
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Questions</DialogTitle>
            <DialogDescription>
              Upload a CSV with question_text and answer_text columns. Valid rows will be imported as answered training questions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Button
              type="button"
              variant="outline"
              onClick={downloadTemplate}
              disabled={isDownloadingTemplate}
            >
              {isDownloadingTemplate ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download />}
              Download CSV template
            </Button>

            <div className="space-y-2">
              <label htmlFor="questions-import-file" className="text-sm font-medium">
                CSV file
              </label>
              <Input
                ref={fileInputRef}
                id="questions-import-file"
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                disabled={isValidating || isImporting}
              />
            </div>

            {isValidating && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Validating CSV...
              </div>
            )}

            {validation && (
              <div className="space-y-3 rounded-md border p-4 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-medium">Selected file: {file?.name}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={clearImportState}
                    disabled={isImporting || isValidating}
                  >
                    Clear
                  </Button>
                </div>
                {validation.valid ? (
                  <div className="space-y-3">
                    <p className="font-medium text-green-700">
                      CSV validated. {validation.row_count} questions are ready to import.
                    </p>
                    {validation.preview.length > 0 && (
                      <div className="space-y-2">
                        <p className="font-medium">Preview</p>
                        <div className="max-h-40 overflow-y-auto rounded border">
                          {validation.preview.map((row, index) => (
                            <div key={`${row.question_text}-${index}`} className="border-b p-2 last:border-b-0">
                              <p className="font-medium">{row.question_text}</p>
                              <p className="text-muted-foreground">{row.answer_text}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="font-medium text-red-700">
                      CSV has {validation.errors.length} validation error{validation.errors.length === 1 ? '' : 's'}.
                    </p>
                    <div className="max-h-48 overflow-y-auto rounded border">
                      {validation.errors.slice(0, 20).map((error, index) => (
                        <div key={`${error.field}-${error.row}-${index}`} className="border-b p-2 last:border-b-0">
                          <span className="font-medium">
                            {error.row ? `Row ${error.row}` : 'File'} {error.field ? `(${error.field})` : ''}
                          </span>
                          : {error.message}
                        </div>
                      ))}
                    </div>
                    {validation.errors.length > 20 && (
                      <p className="text-muted-foreground">Showing the first 20 errors.</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isImporting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={startImport}
              disabled={!file || !validation?.valid || isImporting || isValidating}
            >
              {isImporting && <Loader2 className="h-4 w-4 animate-spin" />}
              Start import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImportQuestionsButton;
